// src/components/Toasts.jsx
// Small stack of dismissible notices in the bottom-left corner. Screen
// readers announce new ones politely. Each notice can carry an onClick
// (e.g. open that fax) and closes itself after a few seconds.
// 3.44.1: each notice is a moving two-or-three-colour fill like the main
// buttons, with a shine that sweeps across, an icon that pops in and a bar
// that runs down until it closes (paused while the mouse is over it).
// Success and info use the theme's colours; error and warning stay red and
// orange in every theme. Palette variables, so dark mode and themes follow.
// A notice may bring its own icon (toast.icon), e.g. 📥 for a received fax.
import { useEffect, useRef, useState } from 'react';
import Emo from './Emo';

const TONES = {
  success: { fill: 'linear-gradient(120deg, var(--c-004d40), var(--c-00796b) 40%, var(--b-2e7d32) 75%, var(--c-00695c))', glow: 'var(--c-00897b-rgb)', icon: '✅' },
  info   : { fill: 'linear-gradient(120deg, var(--c-004d40), var(--c-00695c) 40%, var(--c-1565c0) 75%, var(--c-00796b))', glow: 'var(--c-1565c0-rgb)', icon: '📤' },
  error  : { fill: 'linear-gradient(120deg, var(--c-b71c1c), var(--c-c62828) 40%, var(--c-bf360c) 75%, var(--c-b71c1c))', glow: 'var(--c-c62828-rgb)', icon: '❌' },
  warning: { fill: 'linear-gradient(120deg, var(--c-8d4a00), var(--c-a14400) 45%, var(--c-bf360c) 75%, var(--c-8d4a00))', glow: 'var(--c-e65100-rgb)', icon: '⏳' },
};
const AUTO_CLOSE_MS = 9000;

export default function Toasts({ toasts, onDismiss }) {
  return (
    <div style={S.stack} aria-live="polite" role="status">
      <style>{CSS}</style>
      {toasts.map(t => <Toast key={t.id} toast={t} onDismiss={onDismiss} />)}
    </div>
  );
}

function Toast({ toast, onDismiss }) {
  const tone = TONES[toast.tone] || TONES.info;
  const [paused, setPaused] = useState(false);
  const left = useRef(AUTO_CLOSE_MS), started = useRef(0);

  // Closes itself after AUTO_CLOSE_MS of not being hovered
  useEffect(() => {
    if (paused) return undefined;
    started.current = Date.now();
    const timer = setTimeout(() => onDismiss(toast.id), left.current);
    return () => { clearTimeout(timer); left.current -= Date.now() - started.current; };
  }, [paused, toast.id, onDismiss]);

  return (
    <div className={'cp-toast' + (paused ? ' paused' : '')} style={{ ...S.toast, backgroundImage: tone.fill, '--glow': tone.glow }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <span className="cp-toast-shine" aria-hidden="true" />
      <span className="cp-toast-ico" aria-hidden="true">{typeof (toast.icon || tone.icon) === 'string' ? <Emo e={toast.icon || tone.icon} /> : toast.icon}</span>
      <button onClick={() => { toast.onClick?.(); onDismiss(toast.id); }}
        style={{ ...S.body, cursor: toast.onClick ? 'pointer' : 'default' }}>
        <div style={S.title}>{toast.title}</div>
        {toast.detail && <div style={S.detail}>{toast.detail}</div>}
        {toast.onClick && <div style={S.hint}>{toast.hint || 'View details ›'}</div>}
      </button>
      <button onClick={() => onDismiss(toast.id)} style={S.close} className="cp-toast-x"
        aria-label="Dismiss notification">✕</button>
      <span className="cp-toast-bar" aria-hidden="true" style={{ animationDuration: `${AUTO_CLOSE_MS}ms` }} />
    </div>
  );
}

const S = {
  stack: {
    position: 'fixed', left: 16, bottom: 'calc(16px + var(--cp-bottom-bar, 0px))', zIndex: 1500,   // 3.51.0: above the phone bottom bar
    display: 'flex', flexDirection: 'column', gap: 10,
    width: 'min(370px, calc(100vw - 32px))', pointerEvents: 'none'
  },
  toast: {
    position: 'relative', overflow: 'hidden',
    display: 'flex', alignItems: 'flex-start', gap: 11, padding: '11px 12px 14px',
    borderRadius: 14, color: 'var(--t-ffffff)', backgroundSize: '220% 220%',
    border: '1px solid rgb(var(--c-ffffff-rgb) / 0.22)',
    pointerEvents: 'auto',
  },
  body: {
    position: 'relative', flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none',
    padding: 0, fontFamily: 'inherit', color: 'inherit', textShadow: '0 1px 2px rgb(var(--c-000000-rgb) / 0.25)'
  },
  title: { fontSize: 14, fontWeight: 700, lineHeight: 1.3 },
  detail: { fontSize: 12.5, marginTop: 3, lineHeight: 1.4, overflowWrap: 'anywhere', opacity: 0.95 },
  hint: { fontSize: 11.5, fontWeight: 700, marginTop: 5, letterSpacing: '0.02em' },
  close: {
    position: 'relative', background: 'rgb(var(--c-ffffff-rgb) / 0.16)', border: 'none', cursor: 'pointer', fontSize: 11,
    width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center',
    padding: 0, fontFamily: 'inherit', flexShrink: 0, color: 'inherit'
  },
};

const CSS = `
.cp-toast {
  box-shadow: 0 10px 28px rgb(var(--glow) / 0.35), 0 2px 6px rgb(var(--c-000000-rgb) / 0.15), inset 0 1px 0 rgb(var(--c-ffffff-rgb) / 0.25);
  animation: cpToastIn .55s cubic-bezier(.2,1.4,.4,1) both, cpToastFlow 6s ease-in-out infinite;
  transition: transform .2s ease, box-shadow .2s ease;
}
.cp-toast:hover { transform: scale(1.02); box-shadow: 0 14px 34px rgb(var(--glow) / 0.45), 0 2px 6px rgb(var(--c-000000-rgb) / 0.15), inset 0 1px 0 rgb(var(--c-ffffff-rgb) / 0.3); }
.cp-toast-shine { position: absolute; top: -40%; bottom: -40%; left: -60%; width: 45%; pointer-events: none;
  background: linear-gradient(100deg, transparent, rgb(var(--c-ffffff-rgb) / 0.32), transparent);
  transform: skewX(-18deg); animation: cpToastShine 3.2s ease-in-out .35s infinite; }
.cp-toast-ico { position: relative; flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center;
  font-size: 17px; line-height: 1; background: rgb(var(--c-ffffff-rgb) / 0.92);
  box-shadow: 0 0 0 3px rgb(var(--c-ffffff-rgb) / 0.22), 0 3px 8px rgb(var(--c-000000-rgb) / 0.2);
  animation: cpToastPop .6s cubic-bezier(.3,1.8,.5,1) .12s both, cpToastHalo 2.4s ease-out .8s infinite; }
.cp-toast-x { transition: background .15s ease, transform .2s ease; }
.cp-toast-x:hover { background: rgb(var(--c-ffffff-rgb) / 0.32) !important; transform: rotate(90deg); }
.cp-toast-bar { position: absolute; left: 0; right: 0; bottom: 0; height: 3px; transform-origin: left;
  background: linear-gradient(90deg, rgb(var(--c-ffffff-rgb) / 0.95), rgb(var(--c-ffffff-rgb) / 0.55));
  animation-name: cpToastBar; animation-timing-function: linear; animation-fill-mode: forwards; }
.cp-toast.paused .cp-toast-bar, .cp-toast.paused .cp-toast-shine { animation-play-state: paused; }
@keyframes cpToastIn { 0% { opacity: 0; transform: translateX(-36px) scale(.92); } 100% { opacity: 1; transform: none; } }
@keyframes cpToastFlow { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
@keyframes cpToastShine { 0% { left: -60%; } 55%, 100% { left: 130%; } }
@keyframes cpToastPop { 0% { transform: scale(0) rotate(-30deg); } 100% { transform: none; } }
@keyframes cpToastHalo { 0% { box-shadow: 0 0 0 0 rgb(var(--c-ffffff-rgb) / 0.55), 0 3px 8px rgb(var(--c-000000-rgb) / 0.2); }
  100% { box-shadow: 0 0 0 10px rgb(var(--c-ffffff-rgb) / 0), 0 3px 8px rgb(var(--c-000000-rgb) / 0.2); } }
@keyframes cpToastBar { from { transform: scaleX(1); } to { transform: scaleX(0); } }
`;
